const EPS = 0.01;

export const getAccountKey = (account) => {
  if (!account) return '';
  if (typeof account === 'string') return account.trim();
  return String(account.key ?? account.accountKey ?? '').trim();
};

export const getAccountDisplayParts = (account, accounts = []) => {
  const key = getAccountKey(account);
  if (!key) return { key: '', name: '' };
  if (account && typeof account === 'object') {
    const name = account.name || account.label;
    if (name) return { key, name: String(name).trim() };
  }
  const found = accounts?.length ? findAccountInTree(key, accounts) : null;
  const name = found?.name || found?.label;
  if (name) return { key, name: String(name).trim() };
  return { key, name: '' };
};

export const getAccountDisplayLabel = (account, accounts = []) => {
  const { key, name } = getAccountDisplayParts(account, accounts);
  if (!key) return '';
  return name ? `${key} ${name}`.trim() : key;
};

const findAccountInTree = (accountKey, items) => {
  for (const item of items || []) {
    if (item.key === accountKey) return item;
    if (item.children?.length) {
      const found = findAccountInTree(accountKey, item.children);
      if (found) return found;
    }
  }
  return null;
};

export const defaultCapAmountString = (cap) => {
  if (!cap || cap < EPS) return '';
  return String(Math.round(cap * 100) / 100);
};

export const isIncomeExpenseAccount = (account) => {
  const key = getAccountKey(account);
  return key.startsWith('3') || key.startsWith('4');
};

export const getEntryLineAmount = (entry) => {
  const debit = parseFloat(entry?.debit) || 0;
  const credit = parseFloat(entry?.credit) || 0;
  return Math.max(debit, credit);
};

export const getEntryLineSide = (entry) => {
  const debit = parseFloat(entry?.debit) || 0;
  const credit = parseFloat(entry?.credit) || 0;
  if (debit < EPS && credit < EPS) return null;
  return debit >= credit ? 'debit' : 'credit';
};

export const getIncomeExpenseCapTotal = (journalEntries = []) =>
  (journalEntries || [])
    .filter((e) => isIncomeExpenseAccount(e.account))
    .reduce((sum, e) => sum + getEntryLineAmount(e), 0);

export const parseCostCenterAmountInput = (value) => {
  const raw = String(value ?? '').trim();
  if (raw === '') return 0;
  const normalized = raw.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(normalized);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

export const formatPercentFromAmount = (amount, cap) => {
  if (!cap || cap < EPS) return '0';
  const pct = (amount / cap) * 100;
  return pct.toFixed(2).replace(/\.?0+$/, '') || '0';
};

export const sumRowAmounts = (rows) =>
  (rows || []).reduce((s, r) => s + parseCostCenterAmountInput(r.amount), 0);

export const isAssignmentCapReached = (assignedSum, cap) =>
  cap >= EPS && assignedSum >= cap - EPS;

export const balanceAmountsToTarget = (amounts, target) => {
  if (!amounts?.length) return [];
  const rounded = amounts.map((a) => Math.round(Number(a) || 0));
  const sum = rounded.reduce((s, n) => s + n, 0);
  const diff = Math.round(target) - sum;
  if (diff === 0) return rounded;
  let idx = 0;
  let max = rounded[0] ?? 0;
  for (let i = 1; i < rounded.length; i++) {
    if ((rounded[i] ?? 0) >= max) {
      max = rounded[i];
      idx = i;
    }
  }
  rounded[idx] = (rounded[idx] ?? 0) + diff;
  return rounded;
};

export const hasAnyCostCenterData = (generalRows, byAccountMap) => {
  const rowHasData = (r) =>
    Boolean(r.costCenterId) || parseCostCenterAmountInput(r.amount) >= EPS;
  const generalHas = (generalRows || []).some(rowHasData);
  const byAccountHas = Object.values(byAccountMap || {}).some((rows) =>
    (rows || []).some(rowHasData),
  );
  return generalHas || byAccountHas;
};

export const hasCostCenterSelection = (generalRows, byAccountMap, mode = 'general') => {
  const rowHasCenter = (r) => Boolean(r.costCenterId);
  if (mode === 'byAccount') {
    return Object.values(byAccountMap || {}).some((rows) =>
      (rows || []).some(rowHasCenter),
    );
  }
  return (generalRows || []).some(rowHasCenter);
};

export const countDistinctAssignedCostCenters = ({
  mode = 'general',
  generalRows = [],
  byAccountMap = {},
} = {}) => {
  const ids = new Set();
  const addId = (id) => {
    const normalized = String(id ?? '').trim();
    if (normalized) ids.add(normalized);
  };
  if (mode === 'byAccount') {
    Object.values(byAccountMap || {}).forEach((rows) => {
      (rows || []).forEach((r) => addId(r.costCenterId));
    });
  } else {
    (generalRows || []).forEach((r) => addId(r.costCenterId));
  }
  return ids.size;
};

const linePayloadFromForm = (entry) => {
  const debit = parseFloat(entry.debit) || 0;
  const credit = parseFloat(entry.credit) || 0;
  return { debit, credit, account: entry.account };
};

export const expandGeneralRowsToByAccountMap = (generalRows, journalEntries) => {
  const ieLines = (journalEntries || []).filter((e) => isIncomeExpenseAccount(e.account));
  const globalCap = getIncomeExpenseCapTotal(journalEntries);
  const byAccountMap = {};
  const activeGeneral = (generalRows || []).filter((r) => r.costCenterId);

  for (const line of ieLines) {
    const rankingKey = String(line.ranking);
    const lineCap = getEntryLineAmount(line);
    if (lineCap < EPS) {
      byAccountMap[rankingKey] = [];
      continue;
    }
    if (!activeGeneral.length) {
      byAccountMap[rankingKey] = [];
      continue;
    }
    const rawAmounts = activeGeneral.map((gr) => {
      const globalAmt = parseCostCenterAmountInput(gr.amount);
      return globalCap > EPS ? (globalAmt * lineCap) / globalCap : 0;
    });
    const balanced = balanceAmountsToTarget(rawAmounts, lineCap);
    byAccountMap[rankingKey] = activeGeneral.map((gr, i) => ({
      rowKey: gr.rowKey || `${rankingKey}-${gr.costCenterId}`,
      costCenterId: gr.costCenterId,
      amount: String(balanced[i]),
    }));
  }
  return byAccountMap;
};

export const buildCostCentersForLine = (line, uiRows, catalogList = []) => {
  const payload = linePayloadFromForm(line);
  const cap = getEntryLineAmount(payload);
  const side = getEntryLineSide(payload);
  if (cap < EPS || !side) return [];

  const active = (uiRows || []).filter((r) => r.costCenterId);
  if (!active.length) return [];

  const seen = new Set();
  for (const r of active) {
    if (seen.has(r.costCenterId)) {
      throw new Error('No puede repetir el mismo centro de costo en una línea');
    }
    seen.add(r.costCenterId);
  }

  const rawAmounts = active.map((r) => parseCostCenterAmountInput(r.amount));
  const balanced = balanceAmountsToTarget(rawAmounts, cap);

  return active.map((r, i) => {
    const cc = catalogList.find((c) => String(c.id) === String(r.costCenterId));
    const amt = balanced[i];
    const createdBy =
      r.createdBy === 'rule' || r.createdBy === 'manual' ? r.createdBy : 'manual';
    return {
      costCenterId: String(r.costCenterId),
      label: cc?.name || r.costCenterLabel || r.label,
      debit: side === 'debit' ? amt : 0,
      credit: side === 'credit' ? amt : 0,
      currency: 'CLP',
      createdBy,
    };
  });
};

export const preserveAccountNames = (builtEntries, { accounts = [], fallbackLines = [] } = {}) =>
  (builtEntries || []).map((row) => {
    if (String(row?.accountName || '').trim()) return row;
    const accountKey = String(row?.accountKey || '').trim();
    const fromFallback = (fallbackLines || []).find(
      (line) =>
        line?.ranking === row?.ranking &&
        String(line?.accountKey || '').trim() === accountKey &&
        String(line?.accountName || '').trim(),
    );
    const fromChart = accountKey ? findAccountInTree(accountKey, accounts) : null;
    const accountName = String(
      fromFallback?.accountName || fromChart?.name || '',
    ).trim();
    return accountName ? { ...row, accountName } : row;
  });

export const buildEntriesWithCostCenters = (
  journalEntries,
  { mode, generalRows, byAccountMap },
  catalogList = [],
) => {
  const effectiveMap =
    mode === 'general'
      ? expandGeneralRowsToByAccountMap(generalRows, journalEntries)
      : byAccountMap || {};

  const hasAssignment = hasCostCenterSelection(generalRows, byAccountMap, mode);
  if (!hasAssignment) {
    return { entries: journalEntries, applyingCostCenters: false };
  }

  const entries = journalEntries.map((entry) => {
    let accountKey = '';
    let accountName = '';
    if (typeof entry.account === 'string') {
      accountKey = entry.account;
    } else if (entry.account && typeof entry.account === 'object') {
      accountKey = entry.account.key || '';
      accountName = entry.account.name || '';
    }

    const base = {
      ranking: entry.ranking,
      accountKey,
      accountName,
      debit: parseFloat(entry.debit) || 0,
      credit: parseFloat(entry.credit) || 0,
    };

    if (!isIncomeExpenseAccount(entry.account)) {
      return base;
    }

    const uiRows =
      effectiveMap[String(entry.ranking)] || effectiveMap[entry.ranking] || [];
    if (!uiRows.some((r) => r.costCenterId)) {
      return base;
    }

    try {
      const costCenters = buildCostCentersForLine(entry, uiRows, catalogList);
      return { ...base, costCenters };
    } catch (err) {
      throw err;
    }
  });

  return { entries, applyingCostCenters: true };
};

const normalizeCostCenterIdFromSlice = (cc) => {
  if (!cc || cc.costCenterId == null) return '';
  const raw = cc.costCenterId;
  if (typeof raw === 'object' && raw.$oid) return String(raw.$oid).trim();
  if (typeof raw === 'object' && typeof raw.toString === 'function') {
    return String(raw).trim();
  }
  return String(raw).trim();
};

const resolveCostCenterIdFromSlice = (cc, catalogList = []) => {
  let costCenterId = normalizeCostCenterIdFromSlice(cc);
  if (costCenterId && costCenterId !== 'undefined') return costCenterId;
  const label = String(cc?.label ?? '').trim();
  if (!label || !catalogList?.length) return '';
  const found = catalogList.find(
    (c) => String(c.name ?? '').trim().toLowerCase() === label.toLowerCase(),
  );
  return found?.id != null ? String(found.id).trim() : '';
};

export const hydrateAssignmentFromEntryLines = (
  backendEntries = [],
  catalogList = [],
) => {
  const byAccountMap = {};
  let hasAny = false;

  for (const row of backendEntries) {
    const list = row.costCenters;
    if (!list?.length) continue;
    const rankingKey = String(row.ranking);
    const side = getEntryLineSide({
      debit: row.debit,
      credit: row.credit,
      account: row.accountKey,
    });
    const mapped = list
      .map((cc, i) => {
        const costCenterId = resolveCostCenterIdFromSlice(cc, catalogList);
        if (!costCenterId) return null;
        const rawLabel = String(cc.label ?? '').trim();
        const costCenterLabel =
          rawLabel && rawLabel !== 'manual' && rawLabel !== 'rule' ? rawLabel : undefined;
        const createdBy =
          cc.createdBy === 'rule' || cc.createdBy === 'manual' ? cc.createdBy : undefined;
        return {
          rowKey: `loaded-${rankingKey}-${i}-${costCenterId}`,
          costCenterId,
          costCenterLabel,
          createdBy,
          amount: String(
            side === 'credit' ? Math.round(cc.credit || 0) : Math.round(cc.debit || 0),
          ),
        };
      })
      .filter(Boolean);
    if (!mapped.length) continue;
    hasAny = true;
    byAccountMap[rankingKey] = mapped;
  }

  return {
    mode: 'byAccount',
    generalRows: [createEmptyGeneralRow()],
    byAccountMap,
    hasAny,
  };
};

export const scaleAssignmentForLine = (ranking, debit, credit, byAccountMap) => {
  const rankingKey = String(ranking);
  const rows = byAccountMap[rankingKey] || byAccountMap[ranking];
  if (!rows?.length) return byAccountMap;
  const line = { debit: parseFloat(debit) || 0, credit: parseFloat(credit) || 0 };
  const cap = getEntryLineAmount(line);
  const oldSum = sumRowAmounts(rows);
  if (oldSum < EPS || cap < EPS) return byAccountMap;
  const scaled = balanceAmountsToTarget(
    rows.filter((r) => r.costCenterId).map((r) => (parseCostCenterAmountInput(r.amount) / oldSum) * cap),
    cap,
  );
  let idx = 0;
  const nextRows = rows.map((r) => {
    if (!r.costCenterId) return r;
    const amount = String(scaled[idx]);
    idx += 1;
    return { ...r, amount };
  });
  return { ...byAccountMap, [rankingKey]: nextRows };
};

export const validateCostCenterAssignment = ({
  mode,
  generalRows,
  byAccountMap,
  journalEntries,
}) => {
  const errors = [];
  const warnings = [];

  if (!hasCostCenterSelection(generalRows, byAccountMap, mode)) {
    return { warnings, errors, valid: true, requiresFullAssignment: false };
  }

  const ieLines = (journalEntries || []).filter((e) => isIncomeExpenseAccount(e.account));
  const generalCap = getIncomeExpenseCapTotal(journalEntries);
  const effectiveMap =
    mode === 'general'
      ? expandGeneralRowsToByAccountMap(generalRows, journalEntries)
      : byAccountMap;

  if (generalCap < EPS) {
    errors.push(
      'No hay monto en cuentas de ingreso (3) o gasto (4) para asignar centros de costo.',
    );
    return { warnings, errors, valid: false, requiresFullAssignment: true };
  }

  const checkRows = (rows, label) => {
    const seen = new Set();
    (rows || []).forEach((r, i) => {
      const amt = parseCostCenterAmountInput(r.amount);
      if (r.costCenterId && seen.has(r.costCenterId)) {
        errors.push(`${label}: centro de costo duplicado en la fila ${i + 1}.`);
      }
      if (r.costCenterId) seen.add(r.costCenterId);
      if (r.costCenterId && amt < EPS) {
        errors.push(`${label}: fila ${i + 1} tiene centro sin monto.`);
      }
      if (!r.costCenterId && amt >= EPS) {
        errors.push(`${label}: fila ${i + 1} tiene monto sin centro de costo.`);
      }
    });
  };

  if (mode === 'general') {
    const total = sumRowAmounts(generalRows);
    if (Math.abs(total - generalCap) > EPS) {
      errors.push(
        `La asignación debe sumar el 100% del total (${generalCap.toLocaleString('es-CL')}). Actual: ${total.toLocaleString('es-CL')}.`,
      );
    }
    if (total > generalCap + EPS) {
      errors.push('La suma de centros de costo supera el total imputable.');
    }
    checkRows(generalRows, 'Asignación general');
  }

  ieLines.forEach((line) => {
    const lineAmount = getEntryLineAmount(line);
    if (lineAmount < EPS) return;
    const rows =
      effectiveMap[String(line.ranking)] || effectiveMap[line.ranking] || [];
    const lineSum = sumRowAmounts(rows);
    const label = getAccountKey(line.account) || `Línea ${line.ranking}`;
    if (!rows.some((r) => r.costCenterId)) {
      errors.push(`Cuenta ${label}: debe tener centros de costo al 100% del monto de la línea.`);
    } else if (Math.abs(lineSum - lineAmount) > EPS) {
      errors.push(
        `Cuenta ${label}: los centros de costo deben sumar ${lineAmount.toLocaleString('es-CL')} (actual: ${lineSum.toLocaleString('es-CL')}).`,
      );
    }
    checkRows(rows, `Cuenta ${label}`);
  });

  const globalFromMap = ieLines.reduce((s, line) => {
    if (getEntryLineAmount(line) < EPS) return s;
    return (
      s +
      sumRowAmounts(
        effectiveMap[String(line.ranking)] || effectiveMap[line.ranking] || [],
      )
    );
  }, 0);
  if (Math.abs(globalFromMap - generalCap) > EPS && errors.length === 0) {
    errors.push(
      `El total de centros de costo (${globalFromMap.toLocaleString('es-CL')}) debe igualar el neto 3/4 (${generalCap.toLocaleString('es-CL')}).`,
    );
  }

  return {
    warnings,
    errors,
    valid: errors.length === 0,
    requiresFullAssignment: true,
  };
};

export const getCostCenterSaveConfirmState = (params) => {
  const validation = validateCostCenterAssignment(params);
  const { mode = 'general', generalRows, byAccountMap } = params;
  if (!hasCostCenterSelection(generalRows, byAccountMap, mode)) {
    return { blocking: false, needsConfirm: false, message: '' };
  }
  if (validation.errors.length > 0) {
    return {
      blocking: true,
      needsConfirm: false,
      message: validation.errors[0],
    };
  }
  return { blocking: false, needsConfirm: false, message: '' };
};

export const createEmptyGeneralRow = (defaultAmount = '') => ({
  rowKey: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  costCenterId: '',
  amount: defaultAmount === '' ? '' : String(defaultAmount),
});

export const createEmptyByAccountRow = (defaultAmount = '') => ({
  rowKey: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  costCenterId: '',
  amount: defaultAmount === '' ? '' : String(defaultAmount),
});
